import React, { useMemo } from "react";
import { preferGenerativeCdnUrl } from "../../app/api/imageGenerator";
import { useCollectionBuildSessions } from "./CollectionBuildSessionsContext";
import { collectionBuildSummaryTitle } from "./collectionBuildSession";
import {
  COLLECTION_BUILD_LIBRARY_EMPTY_HINT,
  COLLECTION_BUILD_LIBRARY_GENERATING,
  COLLECTION_BUILD_LIBRARY_HEADLINE,
  COLLECTION_BUILD_LIBRARY_MOODBOARD_LABEL,
  hasCollectionBuildLibraryImageUrl,
} from "./generativePlatformLibraryConstants";
import useGenerativeResultPreviews from "./shared/useGenerativeResultPreviews";

const absolutePreviewSrc = (url: string) => {
  const value = preferGenerativeCdnUrl(url);
  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }
  return "";
};

const CollectionBuildTracker: React.FC<{
  onOpen?: (sessionId: number) => void;
}> = ({ onOpen }) => {
  const { summaries, openSession } = useCollectionBuildSessions();
  const libraryItems = useMemo(
    () =>
      summaries.filter(item =>
        hasCollectionBuildLibraryImageUrl(item.previewUrl)
      ),
    [summaries]
  );
  const previewItems = useMemo(
    () =>
      libraryItems.map(item => ({
        id: String(item.sessionId),
        imageUrl: item.previewUrl,
      })),
    [libraryItems]
  );
  const previews = useGenerativeResultPreviews(previewItems);

  const handleOpen = (sessionId: number) => {
    if (onOpen) {
      onOpen(sessionId);
      return;
    }
    openSession(sessionId);
  };

  if (libraryItems.length === 0) {
    return (
      <div className="rounded-2xl border border-light bg-white p-8 text-center">
        <p className="font-medium text-secondary">
          {COLLECTION_BUILD_LIBRARY_HEADLINE}
        </p>
        <p className="mt-2 text-sm text-tertiary">
          {COLLECTION_BUILD_LIBRARY_EMPTY_HINT}
        </p>
      </div>
    );
  }

  return (
    <div>
      <p className="mb-4 text-sm text-secondary">
        {COLLECTION_BUILD_LIBRARY_HEADLINE}
      </p>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {libraryItems.map(item => {
          const title = collectionBuildSummaryTitle(item);
          const savedLabel = (() => {
            const date = new Date(item.updatedAt || item.createdAt);
            if (Number.isNaN(date.getTime())) {
              return "";
            }
            return date.toLocaleString([], {
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            });
          })();
          const rangeLabel = (item.rangePlan?.items || [])
            .map(row => `${row.quantity} ${row.productType}`)
            .join(" · ");
          return (
            <button
              key={item.sessionId}
              type="button"
              onClick={() => handleOpen(item.sessionId)}
              className="overflow-hidden rounded-2xl border border-light bg-white text-left shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="aspect-[3/2] bg-gray">
                <img
                  src={
                    previews[String(item.sessionId)] ||
                    absolutePreviewSrc(item.previewUrl || "")
                  }
                  alt={title}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="p-3">
                <p className="truncate text-sm font-medium text-secondary">
                  {title}
                </p>
                <p className="mt-1 truncate text-xs text-tertiary">
                  {[
                    item.season,
                    item.lookCount
                      ? `${item.lookCount} look${item.lookCount === 1 ? "" : "s"}`
                      : item.moodboardCount
                        ? COLLECTION_BUILD_LIBRARY_MOODBOARD_LABEL
                        : null,
                    item.status === "generating"
                      ? COLLECTION_BUILD_LIBRARY_GENERATING
                      : savedLabel,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                {rangeLabel ? (
                  <p className="mt-1 truncate text-xs text-tertiary">
                    {rangeLabel}
                  </p>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CollectionBuildTracker;
